import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRole, canDo } from "../lib/roles.jsx";
import StatusChip from "../components/StatusChip";
import CARPanel from "../components/CARPanel";
import EvidenceCustody from "../components/EvidenceCustody";
import ReceiptEnvelope from "../components/ReceiptEnvelope";
import AuthorityGate from "../components/AuthorityGate";
import { AlertCircle, Sparkles, Send, FileSignature, MessageSquareWarning, Gauge } from "lucide-react";

function SectionHeader({ eyebrow, title, right }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
                <p className="label-eyebrow mb-2">{eyebrow}</p>
                <h2 className="font-serif text-3xl text-ink leading-tight">{title}</h2>
            </div>
            {right}
        </div>
    );
}

function Field({ label, value, mono }) {
    return (
        <div>
            <div className="label-eyebrow mb-1">{label}</div>
            <div className={mono ? "data-cell" : "text-sm text-ink"}>{value || "—"}</div>
        </div>
    );
}

export default function CaseDetail() {
    const { id } = useParams();
    const { role, roleId } = useRole();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastReceipt, setLastReceipt] = useState(null);
    const [error, setError] = useState(null);

    const reload = async () => {
        const data = await api.getReferral(id);
        setC(data);
        if (data.receipts?.length) {
            // Show last receipt summary; treat backend "preview" entry as a receipt envelope source.
            const r = data.receipts[data.receipts.length - 1];
            setLastReceipt({
                receipt_hash: r.receipt_hash,
                signer_reference: r.signer_reference,
                node_timestamp: r.node_timestamp,
                verification_status: r.verification_status,
                receipt_type: r.receipt_type,
                outcome: r.outcome,
                actor_role: role.label,
                counterparty_role: "Broker Partner A",
                policy_id: data.submission_ref,
                shared_evidence_refs: (data.evidence?.shared || []).map((e) => e.ref),
                withheld_evidence_count: (data.evidence?.withheld || []).length,
                car_summary: {
                    custody: "See latest CAR summary on action receipt",
                    authority: "See latest CAR summary on action receipt",
                    receipts: "See latest CAR summary on action receipt",
                },
            });
        }
    };

    useEffect(() => {
        setLoading(true);
        api.getReferral(id)
            .then(async (data) => {
                setC(data);
                if (data.receipts?.length) {
                    const last = data.receipts[data.receipts.length - 1];
                    try {
                        const pub = await api.publicReceipt(last.receipt_hash);
                        setLastReceipt(pub);
                    } catch (_) {
                        // ignore — audit trail still reachable
                    }
                }
            })
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load case"))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="text-sm text-ink-muted">Loading case…</div>;
    }
    if (error || !c) {
        return <div className="text-sm text-oxblood">Error: {error || "Case not found"}</div>;
    }

    return (
        <div className="space-y-12" data-testid="case-detail-page">
            {/* Case header */}
            <section>
                <p className="label-eyebrow mb-3">{c.submission_ref}</p>
                <div className="flex flex-wrap items-start justify-between gap-6">
                    <div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-ink">
                            {c.insured_label}
                        </h1>
                        <p className="mt-3 text-base text-ink-secondary">
                            {c.broker_partner}  ·  {c.sector}  ·  Limit requested ${(
                                c.policy_limit_requested_usd / 1_000_000
                            ).toFixed(1)}m
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <StatusChip status={c.status} testId="case-status-chip" />
                        {c.decision?.outcome && <StatusChip status={c.decision.outcome} />}
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                            Active role: {role.label}
                        </span>
                    </div>
                </div>
            </section>

            {/* Broker submission */}
            <section>
                <SectionHeader eyebrow="Broker submission" title="What the platform received" />
                <div className="panel p-6 md:p-7 grid grid-cols-12 gap-5">
                    <div className="col-span-12 md:col-span-3">
                        <Field label="Broker" value={c.broker_partner} />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <Field label="Insured" value={c.insured_label} />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <Field label="Sector" value={c.sector} />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <Field label="Carrier" value={c.carrier} />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                        <Field label="Revenue band" value={c.revenue_band} mono />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                        <Field label="Employee band" value={c.employee_band} mono />
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <Field
                            label="Coverage requested"
                            value={c.coverage_requested?.join("  ·  ")}
                        />
                    </div>
                    <div className="col-span-12 rule-h pt-4">
                        <div className="label-eyebrow mb-3">Risk controls summary</div>
                        <div className="grid grid-cols-12 gap-3">
                            {Object.entries(c.risk_controls_summary || {}).map(([k, v]) => (
                                <div key={k} className="col-span-12 md:col-span-6 border-b border-parchment-rule pb-3">
                                    <div className="label-eyebrow">{k.replace(/_/g, " ")}</div>
                                    <div className="text-sm">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Automated referral flags */}
            <section>
                <SectionHeader
                    eyebrow="Automated referral flags"
                    title="Why the platform did not bind"
                    right={
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                            {c.automated_referral_flags?.length || 0} flag(s)
                        </span>
                    }
                />
                <div className="panel divide-y divide-parchment-rule" data-testid="referral-flags-list">
                    {(c.automated_referral_flags || []).map((f) => (
                        <div
                            key={f.code}
                            className="grid grid-cols-12 gap-3 px-5 md:px-6 py-4"
                            data-testid={`flag-${f.code}`}
                        >
                            <div className="col-span-12 md:col-span-3 self-center">
                                <span className="data-cell text-xs">{f.code}</span>
                            </div>
                            <div className="col-span-9 md:col-span-7 self-center text-sm">
                                {f.label}
                            </div>
                            <div className="col-span-3 md:col-span-2 self-center text-right">
                                <StatusChip
                                    status={f.severity === "high" ? "auth_rejected" : "referral_open"}
                                    label={f.severity.toUpperCase()}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Advisory note */}
            <section>
                <SectionHeader
                    eyebrow="Underwriting assistant"
                    title="Advisory note"
                    right={
                        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ochre">
                            <AlertCircle size={14} strokeWidth={1.5} />
                            Advisory only — no decision authority
                        </span>
                    }
                />
                <div className="panel p-6 md:p-7 space-y-6" data-testid="advisory-note">
                    <div className="flex items-start gap-3">
                        <Sparkles size={16} strokeWidth={1.5} className="text-forest mt-1 shrink-0" />
                        <p className="text-base text-ink leading-relaxed">{c.advisory_note?.summary}</p>
                    </div>
                    <div className="grid grid-cols-12 gap-6">
                        <div className="col-span-12 md:col-span-6">
                            <div className="label-eyebrow mb-3">Missing information</div>
                            <ul className="space-y-2 text-sm">
                                {(c.advisory_note?.missing_information || []).map((m, i) => (
                                    <li key={i} className="border-b border-parchment-rule pb-2 last:border-b-0">
                                        — {m}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            <div className="label-eyebrow mb-3">Suggested referral questions</div>
                            <ul className="space-y-2 text-sm" data-testid="suggested-questions">
                                {(c.advisory_note?.suggested_referral_questions || []).map((q, i) => (
                                    <li key={i} className="border-b border-parchment-rule pb-2 last:border-b-0">
                                        Q{i + 1}. {q}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            <div className="label-eyebrow mb-3">Appetite observations</div>
                            <ul className="space-y-2 text-sm">
                                {(c.advisory_note?.appetite_observations || []).map((m, i) => (
                                    <li key={i}>— {m}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            <div className="label-eyebrow mb-3">Next best actions</div>
                            <ul className="space-y-2 text-sm">
                                {(c.advisory_note?.next_best_actions || []).map((m, i) => (
                                    <li key={i}>— {m}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <p className="text-xs text-ink-muted font-mono uppercase tracking-[0.18em] border-t border-parchment-rule pt-4">
                        {c.advisory_note?.label}
                    </p>
                </div>
            </section>

            {/* Clarification + Evidence */}
            <section className="grid grid-cols-12 gap-6 lg:gap-8">
                <div className="col-span-12 lg:col-span-6">
                    <SectionHeader
                        eyebrow="Broker clarification"
                        title="Request evidence pack"
                    />
                    <AuthorityGate
                        allowed={canDo("request_clarification", roleId)}
                        currentRole={role.label}
                        action="clarification"
                    >
                        <ClarificationForm
                            caseData={c}
                            onSent={(res) => {
                                setLastReceipt(res.receipt);
                                reload();
                            }}
                        />
                    </AuthorityGate>
                    {c.clarification_request && (
                        <div className="panel p-5 mt-5" data-testid="clarification-sent">
                            <div className="label-eyebrow mb-2">Clarification sent</div>
                            <div className="data-cell text-xs mb-3">
                                {c.clarification_request.requested_at}  ·  {c.clarification_request.actor_role}
                            </div>
                            <ul className="space-y-2 text-sm">
                                {c.clarification_request.questions.map((q, i) => (
                                    <li key={i}>Q{i + 1}. {q}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="col-span-12 lg:col-span-6">
                    <SectionHeader
                        eyebrow="Broker evidence"
                        title="Custody & withholding"
                    />
                    <AuthorityGate
                        allowed={canDo("submit_evidence", roleId)}
                        currentRole={role.label}
                        action="evidence"
                    >
                        <EvidenceForm
                            caseData={c}
                            onSent={(res) => {
                                setLastReceipt(res.receipt);
                                reload();
                            }}
                        />
                    </AuthorityGate>
                    <div className="mt-5">
                        <EvidenceCustody evidence={c.evidence} />
                    </div>
                </div>
            </section>

            {/* Underwriter decision */}
            <section>
                <SectionHeader
                    eyebrow="Underwriter decision"
                    title="Approve, modify, decline or escalate"
                    right={
                        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                            <FileSignature size={14} strokeWidth={1.5} />
                            Final authority lives here
                        </span>
                    }
                />
                <AuthorityGate
                    allowed={canDo("record_decision", roleId)}
                    currentRole={role.label}
                    action="decision"
                >
                    <DecisionForm
                        caseData={c}
                        onSent={(res) => {
                            setLastReceipt(res.receipt);
                            reload();
                        }}
                    />
                </AuthorityGate>
                {c.decision && (
                    <div className="panel p-6 mt-5" data-testid="decision-recorded">
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-eyebrow">Decision recorded</div>
                            <StatusChip status={c.decision.outcome} />
                        </div>
                        <div className="data-cell text-xs mb-3">
                            {c.decision.decided_at} · {c.decision.actor_role}
                        </div>
                        <p className="text-sm">{c.decision.rationale}</p>
                        {c.decision.pricing_note && (
                            <p className="text-sm mt-2"><span className="label-eyebrow">Pricing:</span> {c.decision.pricing_note}</p>
                        )}
                        {c.decision.structure_note && (
                            <p className="text-sm mt-2"><span className="label-eyebrow">Structure:</span> {c.decision.structure_note}</p>
                        )}
                        {c.decision.exclusions?.length > 0 && (
                            <p className="text-sm mt-2"><span className="label-eyebrow">Exclusions:</span> {c.decision.exclusions.join(", ")}</p>
                        )}
                    </div>
                )}
            </section>

            {/* Appetite feedback */}
            <section>
                <SectionHeader
                    eyebrow="Appetite feedback loop"
                    title="Signal to product / portfolio"
                    right={
                        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                            <Gauge size={14} strokeWidth={1.5} />
                            Portfolio learning
                        </span>
                    }
                />
                <AuthorityGate
                    allowed={canDo("appetite_feedback", roleId)}
                    currentRole={role.label}
                    action="appetite"
                >
                    <AppetiteForm
                        caseData={c}
                        onSent={(res) => {
                            setLastReceipt(res.receipt);
                            reload();
                        }}
                    />
                </AuthorityGate>
                {c.appetite_feedback && (
                    <div className="panel p-6 mt-5" data-testid="appetite-recorded">
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-eyebrow">Appetite signal recorded</div>
                            <StatusChip
                                status={c.appetite_feedback.signal === "within_appetite" ? "approve" : c.appetite_feedback.signal === "out_of_appetite" ? "decline" : "modify"}
                                label={c.appetite_feedback.signal.replace(/_/g, " ").toUpperCase()}
                            />
                        </div>
                        <div className="data-cell text-xs mb-3">
                            {c.appetite_feedback.logged_at} · {c.appetite_feedback.actor_role}
                        </div>
                        <p className="text-sm">{c.appetite_feedback.commentary}</p>
                    </div>
                )}
            </section>

            {/* Latest receipt */}
            {lastReceipt && (
                <section>
                    <SectionHeader eyebrow="Latest governed action" title="Signed receipt" />
                    <ReceiptEnvelope receipt={lastReceipt} />
                </section>
            )}

            {/* All receipts list */}
            {c.receipts?.length > 0 && (
                <section>
                    <SectionHeader eyebrow="Audit trail" title={`${c.receipts.length} receipt(s) recorded`} />
                    <div className="panel divide-y divide-parchment-rule">
                        {c.receipts.map((r) => (
                            <div
                                key={r.id}
                                className="grid grid-cols-12 gap-3 px-5 md:px-6 py-3"
                                data-testid={`receipt-row-${r.id}`}
                            >
                                <div className="col-span-12 md:col-span-3 data-cell text-xs">
                                    {r.receipt_type}
                                </div>
                                <div className="col-span-12 md:col-span-5 data-cell text-xs break-all">
                                    {r.receipt_hash}
                                </div>
                                <div className="col-span-6 md:col-span-2 self-center">
                                    <StatusChip status={r.verification_status} />
                                </div>
                                <div className="col-span-6 md:col-span-2 self-center text-right">
                                    <a
                                        href={`/r/${encodeURIComponent(r.receipt_hash)}`}
                                        className="nav-link"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Public →
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function ClarificationForm({ caseData, onSent }) {
    const suggested = caseData.advisory_note?.suggested_referral_questions || [];
    const [questions, setQuestions] = useState(suggested.slice(0, 4));
    const [notes, setNotes] = useState("Please return evidence references within 5 business days.");
    const [busy, setBusy] = useState(false);

    const toggle = (q) => {
        setQuestions((cur) =>
            cur.includes(q) ? cur.filter((x) => x !== q) : [...cur, q]
        );
    };
    const submit = async () => {
        if (questions.length === 0) return;
        setBusy(true);
        try {
            const res = await api.requestClarification(caseData.id, { questions, notes });
            onSent(res);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="panel p-6" data-testid="clarification-form">
            <div className="label-eyebrow mb-3">Select questions to send</div>
            <ul className="space-y-2 mb-5">
                {suggested.map((q, i) => (
                    <li key={i}>
                        <label className="flex items-start gap-3 cursor-pointer text-sm">
                            <input
                                type="checkbox"
                                className="mt-1 accent-forest"
                                checked={questions.includes(q)}
                                onChange={() => toggle(q)}
                                data-testid={`clarification-q-${i}`}
                            />
                            <span>Q{i + 1}. {q}</span>
                        </label>
                    </li>
                ))}
            </ul>
            <div className="label-eyebrow mb-2">Underwriter notes</div>
            <textarea
                className="textarea-line mb-4"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="clarification-notes"
            />
            <button
                className="btn-primary"
                disabled={busy || questions.length === 0}
                onClick={submit}
                data-testid="clarification-send-btn"
            >
                <Send size={14} strokeWidth={1.5} />
                {busy ? "Sending…" : "Send & sign clarification receipt"}
            </button>
        </div>
    );
}

const DEFAULT_EVIDENCE = [
    { ref: "ev:mfa-attest-2026-001", label: "MFA enforcement attestation (signed)", custody: "shared", custodian: "Broker Partner A" },
    { ref: "ev:backup-arch-2026-001", label: "Backup architecture statement with immutable copy diagram", custody: "shared", custodian: "Broker Partner A" },
    { ref: "ev:edr-coverage-report", label: "EDR coverage report (95% endpoints)", custody: "shared", custodian: "SME Technology Client" },
    { ref: "ev:incident-clarification", label: "Prior incident clarification narrative", custody: "shared", custodian: "Broker Partner A" },
    { ref: "ev:customer-contracts-redacted", label: "Top-3 customer contracts (full text)", custody: "withheld", custodian: "SME Technology Client" },
    { ref: "ev:soc-logs-raw", label: "Raw SOC logs (90 days)", custody: "withheld", custodian: "SME Technology Client" },
];

function EvidenceForm({ caseData, onSent }) {
    const [items, setItems] = useState(DEFAULT_EVIDENCE);
    const [busy, setBusy] = useState(false);

    const flip = (idx) => {
        setItems((cur) =>
            cur.map((it, i) =>
                i === idx ? { ...it, custody: it.custody === "shared" ? "withheld" : "shared" } : it
            )
        );
    };
    const submit = async () => {
        setBusy(true);
        try {
            const res = await api.submitEvidence(caseData.id, { items });
            onSent(res);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="panel p-6" data-testid="evidence-form">
            <div className="label-eyebrow mb-3">Broker decides what to share</div>
            <ul className="space-y-2 mb-5">
                {items.map((it, i) => (
                    <li key={it.ref} className="border-b border-parchment-rule pb-2 last:border-b-0 flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm">{it.label}</div>
                            <div className="data-cell text-xs text-ink-muted">{it.ref} · {it.custodian}</div>
                        </div>
                        <button
                            onClick={() => flip(i)}
                            className="font-mono text-[11px] uppercase tracking-[0.16em] px-2 py-1 border border-parchment-rule hover:border-ink"
                            data-testid={`evidence-toggle-${i}`}
                        >
                            {it.custody === "shared" ? "Share" : "Withhold"}
                        </button>
                    </li>
                ))}
            </ul>
            <button
                className="btn-primary"
                disabled={busy}
                onClick={submit}
                data-testid="evidence-submit-btn"
            >
                <Send size={14} strokeWidth={1.5} />
                {busy ? "Recording…" : "Submit evidence & sign custody receipt"}
            </button>
        </div>
    );
}

function DecisionForm({ caseData, onSent }) {
    const [outcome, setOutcome] = useState("modify");
    const [rationale, setRationale] = useState(
        "Accept on a modified basis. MFA attestation and backup architecture satisfy minimum hygiene; EDR coverage at 95% is acceptable subject to a 12-month review. Ransomware extension declined as outside published appetite; sub-limit applied to contractual liability."
    );
    const [pricing, setPricing] = useState("Base premium +12% loading for contractual liability profile; remove broker discount.");
    const [structure, setStructure] = useState("Add USD 1.5m sub-limit on contractual liability; exclude ransomware extension; impose 72hr breach-notification co-operation clause.");
    const [exclusionsText, setExclusionsText] = useState("Ransomware extension\nState-sponsored attack");
    const [requiresManager, setRequiresManager] = useState(true);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const exclusions = exclusionsText.split("\n").map((s) => s.trim()).filter(Boolean);
            const res = await api.submitDecision(caseData.id, {
                outcome,
                rationale,
                pricing_note: pricing,
                structure_note: structure,
                exclusions,
                requires_manager_approval: requiresManager,
            });
            onSent(res);
        } finally {
            setBusy(false);
        }
    };

    const Outcome = ({ value, label }) => (
        <button
            onClick={() => setOutcome(value)}
            data-testid={`decision-outcome-${value}`}
            className={
                "px-3 py-2 font-mono text-[11px] uppercase tracking-[0.16em] border " +
                (outcome === value
                    ? "bg-forest text-parchment border-forest"
                    : "text-ink border-parchment-rule hover:border-ink")
            }
        >
            {label}
        </button>
    );

    return (
        <div className="panel p-6 space-y-5" data-testid="decision-form">
            <div className="flex flex-wrap gap-2">
                <Outcome value="approve" label="Approve" />
                <Outcome value="modify" label="Approve · modified" />
                <Outcome value="decline" label="Decline" />
                <Outcome value="refer_higher_authority" label="Refer higher authority" />
            </div>
            <div>
                <div className="label-eyebrow mb-2">Rationale</div>
                <textarea
                    className="textarea-line"
                    rows={4}
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    data-testid="decision-rationale"
                />
            </div>
            <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 md:col-span-6">
                    <div className="label-eyebrow mb-2">Pricing note</div>
                    <textarea
                        className="textarea-line"
                        rows={3}
                        value={pricing}
                        onChange={(e) => setPricing(e.target.value)}
                        data-testid="decision-pricing"
                    />
                </div>
                <div className="col-span-12 md:col-span-6">
                    <div className="label-eyebrow mb-2">Structure note</div>
                    <textarea
                        className="textarea-line"
                        rows={3}
                        value={structure}
                        onChange={(e) => setStructure(e.target.value)}
                        data-testid="decision-structure"
                    />
                </div>
                <div className="col-span-12">
                    <div className="label-eyebrow mb-2">Exclusions (one per line)</div>
                    <textarea
                        className="textarea-line"
                        rows={2}
                        value={exclusionsText}
                        onChange={(e) => setExclusionsText(e.target.value)}
                        data-testid="decision-exclusions"
                    />
                </div>
            </div>
            <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input
                    type="checkbox"
                    className="accent-forest"
                    checked={requiresManager}
                    onChange={(e) => setRequiresManager(e.target.checked)}
                    data-testid="decision-requires-manager"
                />
                Requires manager authority before release of terms
            </label>
            <button
                className="btn-primary"
                disabled={busy}
                onClick={submit}
                data-testid="decision-record-btn"
            >
                <FileSignature size={14} strokeWidth={1.5} />
                {busy ? "Recording…" : "Record decision & sign receipt"}
            </button>
        </div>
    );
}

function AppetiteForm({ caseData, onSent }) {
    const [signal, setSignal] = useState("appetite_review_needed");
    const [commentary, setCommentary] = useState(
        "Recurring requests for ransomware extension on technology accounts at this revenue tier suggest the published appetite band should be revisited. Contractual liability profile is becoming more aggressive in customer contracts; recommend a portfolio review."
    );
    const [coverages, setCoverages] = useState("Ransomware Extension\nContractual Liability");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const res = await api.submitAppetiteFeedback(caseData.id, {
                signal,
                commentary,
                affected_coverages: coverages.split("\n").map((s) => s.trim()).filter(Boolean),
            });
            onSent(res);
        } finally {
            setBusy(false);
        }
    };

    const Signal = ({ v, label }) => (
        <button
            onClick={() => setSignal(v)}
            data-testid={`appetite-signal-${v}`}
            className={
                "px-3 py-2 font-mono text-[11px] uppercase tracking-[0.16em] border " +
                (signal === v
                    ? "bg-forest text-parchment border-forest"
                    : "text-ink border-parchment-rule hover:border-ink")
            }
        >
            {label}
        </button>
    );

    return (
        <div className="panel p-6 space-y-5" data-testid="appetite-form">
            <div className="flex flex-wrap gap-2">
                <Signal v="within_appetite" label="Within appetite" />
                <Signal v="appetite_review_needed" label="Review needed" />
                <Signal v="out_of_appetite" label="Out of appetite" />
            </div>
            <div>
                <div className="label-eyebrow mb-2">Affected coverages</div>
                <textarea
                    className="textarea-line"
                    rows={2}
                    value={coverages}
                    onChange={(e) => setCoverages(e.target.value)}
                    data-testid="appetite-coverages"
                />
            </div>
            <div>
                <div className="label-eyebrow mb-2">Commentary</div>
                <textarea
                    className="textarea-line"
                    rows={4}
                    value={commentary}
                    onChange={(e) => setCommentary(e.target.value)}
                    data-testid="appetite-commentary"
                />
            </div>
            <button
                className="btn-primary"
                disabled={busy}
                onClick={submit}
                data-testid="appetite-submit-btn"
            >
                <MessageSquareWarning size={14} strokeWidth={1.5} />
                {busy ? "Recording…" : "Record appetite signal & sign receipt"}
            </button>
        </div>
    );
}
